import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

/** Human-readable labels for mutation keys. */
const MUTATION_LABELS: Record<string, string> = {
  createSubscription: "subscription creation",
  updateSubscription: "subscription update",
  deleteSubscription: "subscription deletion",
  createCategory: "category creation",
  updateCategory: "category update",
  deleteCategory: "category deletion",
  createPayment: "payment creation",
  deletePayment: "payment deletion",
  createLifecycleEvent: "lifecycle event creation",
  updateLifecycleEvent: "lifecycle event update",
  deleteLifecycleEvent: "lifecycle event deletion",
  createSubscriptionPeriod: "period creation",
  updateSubscriptionPeriod: "period update",
  deleteSubscriptionPeriod: "period deletion",
  addSubscriptionTag: "tag addition",
  removeSubscriptionTag: "tag removal",
};

function getMutationLabel(mutationKey: unknown): string {
  if (Array.isArray(mutationKey) && typeof mutationKey[0] === "string") {
    return MUTATION_LABELS[mutationKey[0]] || mutationKey[0];
  }
  return "change";
}

/**
 * Global component that monitors the React Query mutation cache for
 * previously-paused mutations that complete after reconnecting.
 *
 * - On success: batches into a single "All changes synced" toast
 * - On error: shows a per-mutation error toast with Retry action
 */
export const OfflineSyncToast: React.FC = () => {
  const queryClient = useQueryClient();
  // Track mutation IDs that were seen as paused
  const pausedIds = useRef(new Set<number>());
  // Debounce success toasts
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncCountRef = useRef(0);

  useEffect(() => {
    const mutationCache = queryClient.getMutationCache();

    const unsubscribe = mutationCache.subscribe((event) => {
      if (event.type !== "updated") return;
      const mutation = event.mutation;
      if (!mutation) return;

      const id = mutation.mutationId;
      const state = mutation.state;

      // Track mutations that become paused
      if (state.isPaused) {
        pausedIds.current.add(id);
        return;
      }

      // Only care about previously-paused mutations that have now resolved
      if (!pausedIds.current.has(id)) return;

      if (state.status === "success") {
        pausedIds.current.delete(id);
        syncCountRef.current++;

        // Debounce: batch multiple sync successes into one toast
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
          if (syncCountRef.current > 0) {
            toast.success("All changes synced", { duration: 3000 });
            syncCountRef.current = 0;
          }
        }, 500);
      }

      if (state.status === "error") {
        pausedIds.current.delete(id);

        const label = getMutationLabel(mutation.options.mutationKey);
        const errorMessage =
          state.error instanceof Error
            ? state.error.message
            : "An unexpected error occurred";

        toast.error(`Could not sync ${label}`, {
          description: errorMessage,
          action: {
            label: "Retry",
            onClick: () => {
              void queryClient.getMutationCache().notify({
                type: "updated",
                mutation,
              } as any);
              void mutation.execute(state.variables as any);
            },
          },
        });
      }
    });

    return () => {
      unsubscribe();
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [queryClient]);

  return null;
};
